import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

export enum PortalInteractionPresentation {
  GLOBAL = 'global',
  INLINE = 'inline',
}

interface ActiveInteraction {
  id: number;
  showOverlay: boolean;
  status: string;
  presentation: PortalInteractionPresentation;
}

export interface BeginInteractionOptions {
  showOverlay?: boolean;
}

export interface PortalInteractionLockContextValue {
  activeStatus: string | null;
  activePresentation: PortalInteractionPresentation | null;
  beginInteraction: (
    status: string,
    presentationOrOptions?:
      | PortalInteractionPresentation
      | BeginInteractionOptions
  ) => number;
  endInteraction: (interactionId: number) => void;
  isInteractionLocked: boolean;
  isOverlayVisible: boolean;
}

const PortalInteractionLockContext =
  createContext<PortalInteractionLockContextValue | null>(null);

interface PortalInteractionLockProviderProps {
  children: ReactNode;
}

export const PortalInteractionLockProvider = ({
  children,
}: PortalInteractionLockProviderProps) => {
  const [activeInteractions, setActiveInteractions] = useState<
    ActiveInteraction[]
  >([]);
  const nextInteractionId = useRef(0);

  const beginInteraction = useCallback(
    (
      status: string,
      presentationOrOptions:
        | PortalInteractionPresentation
        | BeginInteractionOptions = PortalInteractionPresentation.GLOBAL
    ) => {
      nextInteractionId.current += 1;
      const interactionId = nextInteractionId.current;
      const presentation =
        typeof presentationOrOptions === 'string'
          ? presentationOrOptions
          : PortalInteractionPresentation.GLOBAL;
      const showOverlay =
        typeof presentationOrOptions === 'string'
          ? true
          : presentationOrOptions.showOverlay !== false;
      setActiveInteractions((currentInteractions) => [
        ...currentInteractions,
        {
          id: interactionId,
          presentation,
          showOverlay,
          status,
        },
      ]);
      return interactionId;
    },
    []
  );

  const endInteraction = useCallback((interactionId: number) => {
    setActiveInteractions((currentInteractions) =>
      currentInteractions.filter(
        (interaction) => interaction.id !== interactionId
      )
    );
  }, []);

  const contextValue = useMemo<PortalInteractionLockContextValue>(() => {
    const activeInteraction = activeInteractions[activeInteractions.length - 1];

    return {
      activeStatus: activeInteraction?.status ?? null,
      activePresentation: activeInteraction?.presentation ?? null,
      beginInteraction,
      endInteraction,
      isInteractionLocked: activeInteractions.length > 0,
      isOverlayVisible: activeInteractions.some(
        (interaction) => interaction.showOverlay
      ),
    };
  }, [activeInteractions, beginInteraction, endInteraction]);

  return (
    <PortalInteractionLockContext.Provider value={contextValue}>
      {children}
    </PortalInteractionLockContext.Provider>
  );
};

export const usePortalInteractionLock = () => {
  const context = useContext(PortalInteractionLockContext);
  if (!context) {
    throw new Error(
      'usePortalInteractionLock must be used within PortalInteractionLockProvider'
    );
  }
  return context;
};

export const useOptionalPortalInteractionLock = () =>
  useContext(PortalInteractionLockContext);
